import { inject, Injectable } from '@angular/core';
import { DateConverter } from '@shared-helpers/dateConverter';
import { CallbackStatus } from '@shared-model/phone/CallbackStatus';
import { CallBackStatusModel } from '@shared-model/phone/CallBackStatusModel';
import { CallbackStatusType } from '@shared-model/phone/CallbackStatusType';
import { MemberCallbackResult } from '@shared-results/member/MemberCallbackResult';
import { ServiceOperationOutcome } from '@shared-results/ServiceOperationOutcome';
import { BaseHttpService } from '@shared-services/base-http-service';
import { MemberService } from '@shared-services/member.service';
import { Observer } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';

@Injectable({
  providedIn: 'root' // Single instance provided in root
})
export class CallbackService extends BaseHttpService {
  private callbackUrl = '/callback/';

  private memberService = inject(MemberService);

  private statusConfig: Map<CallbackStatus, CallBackStatusModel> = null;

  defaultPollingTime: number = 1500;
  defaultCloseTimer: number = 5000;

  constructor() { super("CallbackService"); }

  getStatusConfig(status: CallbackStatus): CallBackStatusModel {
    if (this.statusConfig == null) {
      this.statusConfig = new Map<CallbackStatus, CallBackStatusModel>();

      this.statusConfig.set(CallbackStatus.Ready, { Status: CallbackStatus.Ready, Type: CallbackStatusType.Completed, Label: "Call Me Now", SvgHref: "" });
      this.statusConfig.set(CallbackStatus.Unknown, { Status: CallbackStatus.Unknown, Type: CallbackStatusType.Waiting, Label: "Waiting", SvgHref: "#icon-wait" });
      this.statusConfig.set(CallbackStatus.Requested, { Status: CallbackStatus.Requested, Type: CallbackStatusType.Waiting, Label: "Requested", SvgHref: "#icon-wait" });
      this.statusConfig.set(CallbackStatus.Processing, { Status: CallbackStatus.Processing, Type: CallbackStatusType.Waiting, Label: "Processing", SvgHref: "#icon-wait" });
      this.statusConfig.set(CallbackStatus.CallInProgress, { Status: CallbackStatus.CallInProgress, Type: CallbackStatusType.Waiting, Label: "Call In Progress", SvgHref: "#icon-wait" });
      this.statusConfig.set(CallbackStatus.Cancelled, { Status: CallbackStatus.Cancelled, Type: CallbackStatusType.Completed, Label: "Call Cancelled", SvgHref: "" });
      this.statusConfig.set(CallbackStatus.Error, { Status: CallbackStatus.Error, Type: CallbackStatusType.Error, Label: "Error", SvgHref: "" });
      this.statusConfig.set(CallbackStatus.CallNotAnswered, { Status: CallbackStatus.CallNotAnswered, Type: CallbackStatusType.Completed, Label: "Call Not Answered", SvgHref: "" });
      this.statusConfig.set(CallbackStatus.CallNotAccepted, { Status: CallbackStatus.CallNotAccepted, Type: CallbackStatusType.Completed, Label: "Call Not Accepted", SvgHref: "" });
      this.statusConfig.set(CallbackStatus.AccountNotEntered, { Status: CallbackStatus.AccountNotEntered, Type: CallbackStatusType.Completed, Label: "Account Not Entered", SvgHref: "" });
      this.statusConfig.set(CallbackStatus.CustomerBusy, { Status: CallbackStatus.CustomerBusy, Type: CallbackStatusType.Completed, Label: "Your Line is Busy", SvgHref: "" });
      this.statusConfig.set(CallbackStatus.ExpertBusy, { Status: CallbackStatus.ExpertBusy, Type: CallbackStatusType.Completed, Label: "Expert Busy", SvgHref: "" });
      this.statusConfig.set(CallbackStatus.InsufficientCredit, { Status: CallbackStatus.InsufficientCredit, Type: CallbackStatusType.Completed, Label: "Insufficient Credit", SvgHref: "" });
      this.statusConfig.set(CallbackStatus.ExpertNotAccepted, { Status: CallbackStatus.ExpertNotAccepted, Type: CallbackStatusType.Completed, Label: "Expert Not Accepted", SvgHref: "" });
      this.statusConfig.set(CallbackStatus.ExpertNoAnswer, { Status: CallbackStatus.ExpertNoAnswer, Type: CallbackStatusType.Completed, Label: "Expert No Answer", SvgHref: "" });
      this.statusConfig.set(CallbackStatus.CustomerHungUp, { Status: CallbackStatus.CustomerHungUp, Type: CallbackStatusType.Completed, Label: "Customer Hung Up", SvgHref: "" });
      this.statusConfig.set(CallbackStatus.CallAnswered, { Status: CallbackStatus.CallAnswered, Type: CallbackStatusType.CompletedAndClose, Label: "Call Answered", SvgHref: "" });
      this.statusConfig.set(CallbackStatus.CallCompleted, { Status: CallbackStatus.CallCompleted, Type: CallbackStatusType.CompletedAndClose, Label: "Call Completed", SvgHref: "" });
    }

    return this.statusConfig.get(status);
  }

  requestNewCallback(callBackNumber: string, expertId: number, bundleMinutes: number, observerOrNext?: Partial<Observer<MemberCallbackResult>> | ((value: MemberCallbackResult) => void)): void {
    let url: string = `${this.callbackUrl}RequestNewCallback`;
    let body: string = JSON.stringify({ callBackNumber: callBackNumber, expertId: expertId, bundleMinutes: bundleMinutes });
    let options = this.httpOptions;
    let client = this.http?.post<MemberCallbackResult>(url, body, options).pipe(
      timeout(this.httpTimeout),
      map((item) => DateConverter.stringsToDates(item)),
      switchMap((result) => { return this.logErrorIfNotSuccess(url, body, options, result) }),
      catchError(this.errorService?.handleHttpError<MemberCallbackResult>(url, body, options, this))
    );
    this.addSubscriptions(client, observerOrNext, (result) => {
      if (result?.ServiceOperationOutcome == ServiceOperationOutcome.Success) {
        // Request succeeded
        this.memberService?.cacheMemberData(result?.Member);
      }
    });
  }

  getCallbackStatus(expertId: number, observerOrNext?: Partial<Observer<MemberCallbackResult>> | ((value: MemberCallbackResult) => void)): void {
    let url: string = `${this.callbackUrl}GetCallbackStatus`;
    let body: string = expertId ? JSON.stringify({ expertId: expertId }) : null;
    let options = this.httpOptions;
    let client = this.http?.post<MemberCallbackResult>(url, body, options).pipe(
      timeout(this.httpTimeout),
      map((item) => DateConverter.stringsToDates(item)),
      catchError(this.errorService?.handleHttpError<MemberCallbackResult>(url, body, options, this))
    );
    this.addSubscriptions(client, observerOrNext, (result) => {
      if (result?.ServiceOperationOutcome == ServiceOperationOutcome.Success) {
        // Request succeeded
        this.memberService?.cacheMemberData(result?.Member);
      }
    });
  }

  cancelCallback(observerOrNext?: Partial<Observer<MemberCallbackResult>> | ((value: MemberCallbackResult) => void)): void {
    let url: string = `${this.callbackUrl}CancelCallback`;
    let body: string = null;
    let options = this.httpOptions;
    let client = this.http?.post<MemberCallbackResult>(url, body, options).pipe(
      timeout(this.httpTimeout),
      map((item) => DateConverter.stringsToDates(item)),
      catchError(this.errorService?.handleHttpError<MemberCallbackResult>(url, body, options, this))
    );
    this.addSubscriptions(client, observerOrNext, (result) => {
      if (result?.ServiceOperationOutcome == ServiceOperationOutcome.Success) {
        // Request succeeded
        this.memberService?.cacheMemberData(result?.Member);
      }
    });
  }
}
